import { faTrash } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { GEO_TARGETS_TYPES, IForm, IGeoTargetsForm, IGeoTargetsListItem } from "form/model"
import {
  deleteSelectedGeoTargets,
  getDistanceUnit,
  getGeoTargetsFormSafely,
  hasGeoTargets,
  setGeoTargetsForm
} from "form/tools/geo-targets"
import { getRadiusInUserUnit } from "form/tools/geo-targets-coordinates"
import { getGeoTargetListItems, getItemIdsFromSelection, getSelectionFromItemIds } from "form/tools/geo-targets-list"
import { messageSVC } from "message/service"
import React from "react"
import { connect } from "react-redux"
import { IGeoShape, SHAPE_TYPES } from "shape/model"
import { getShapeTitle, sortShapeType } from "shape/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { getDataGridTexts } from "ui/tools/list"
import { GeoTargetType } from "./GeoTargetType"
import { GeoTargetTypeFilter } from "./GeoTargetTypeFilter"
import { GeoTargetsListActions } from "./GeoTargetsListActions"
import { GeoTargetsExportButton } from "./export/GeoTargetsExportButton"
import { GeoTargetsImportButton } from "./import/GeoTargetsImportButton"

interface IGeoTargetsListProps {
  form: IForm
  onItemClick: (item: IGeoTargetsListItem) => any
}

interface IGeoTargetsListConnectProps {
  geoTargetsForm: IGeoTargetsForm | null
  contentWidth: number
}

const GeoTargetsListFn = React.memo(
  ({ form, geoTargetsForm, contentWidth, onItemClick }: IGeoTargetsListProps & IGeoTargetsListConnectProps) => {
    const [typeFilter, setTypeFilter] = React.useState<GEO_TARGETS_TYPES[]>([])
    if (!geoTargetsForm) {
      return null
    }
    const { selection, isDeletingSelection, toUpdateEntityId } = geoTargetsForm
    const selectedItemIds = getItemIdsFromSelection(selection)
    const items: IGeoTargetsListItem[] = getGeoTargetListItems(form.geoTargets, typeFilter)
    const distanceUnit = translate(`label.distance-unit.${getDistanceUnit()}`)
    const columns: Array<GridColDef<IGeoTargetsListItem>> = [
      {
        field: "type",
        headerName: translate("label.type"),
        flex: 2,
        sortComparator: (_1, _2, cell1, cell2) => sortShapeType(cell1.value as SHAPE_TYPES, cell2.value as SHAPE_TYPES),
        renderCell: params => <GeoTargetType id={`${params.row.id}-type`} type={params.row.type as GEO_TARGETS_TYPES} />
      },
      {
        field: "name",
        headerName: translate("label.name"),
        flex: 3,
        renderCell: params => (
          <Typography id={`${params.row.id}-name`} color="textPrimary" fontSize=".75rem">
            {params.row.type === GEO_TARGETS_TYPES.COORDINATE
              ? params.row.name
              : getShapeTitle({
                  shape: params.row.shape as IGeoShape,
                  withType: false
                })}
          </Typography>
        )
      },
      {
        field: "radius",
        headerName: `${translate("label.radius")} (${distanceUnit})`,
        flex: 2,
        renderCell: params => (
          <Typography id={`${params.row.id}-radius`} color="textPrimary" fontSize=".75rem">
            {typeof params.row.radius === "number" ? getRadiusInUserUnit(params.row.radius) : ""}
          </Typography>
        )
      },
      {
        field: "latitude",
        headerName: translate("label.latitude"),
        flex: 2,
        renderCell: params => (
          <Typography id={`${params.row.id}-latitude`} color="textPrimary" fontSize=".75rem">
            {params.row.latitude !== null ? Number(params.row.latitude).toFixed(6) : ""}
          </Typography>
        )
      },
      {
        field: "longitude",
        headerName: translate("label.longitude"),
        flex: 2,
        renderCell: params => (
          <Typography id={`${params.row.id}-longitude`} color="textPrimary" fontSize=".75rem">
            {params.row.longitude !== null ? Number(params.row.longitude).toFixed(6) : ""}
          </Typography>
        )
      },
      {
        field: "actions",
        headerName: translate("label.actions"),
        flex: 1,
        align: "right",
        renderCell: params => <GeoTargetsListActions item={params.row} isDeletingSelection={isDeletingSelection} />
      }
    ]
    const listHeightInRem: string =
      items.length === 0 ? "9rem" : items.length > 15 ? "35rem" : `calc(7rem + ${items.length * 2}rem)`
    return (
      <Box
        id="geo-target-list"
        sx={{
          "position": "relative",
          "width": "100%",
          "minWidth": "100%",
          "maxWidth": "100%",
          "height": "100%",
          "minHeight": "100%",
          "maxHeight": "100%",
          "display": "flex",
          "flexDirection": "column",
          "alignItems": "center",
          "justifyContent": "center",
          "transition": "all 400ms ease",
          "& > *": {
            width: "100%"
          },
          "& .MuiDataGrid-selectedRowCount": {
            visibility: "hidden"
          },
          "& .MuiDataGrid-root": {
            border: "none"
          },
          "& .MuiDataGrid-cell": {
            fontSize: ".8rem",
            fontWeight: 300
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer"
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "inherit"
          },
          "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
            {
              outline: "none !important"
            }
        }}>
        <FlexBox
          jc="start"
          vertical
          sx={{
            margin: ".5rem",
            mb: "1rem"
          }}>
          <Typography color="textPrimary" fontSize=".85rem" width="100%">
            {translate("campaign.geo-targets.list.table.name")}
          </Typography>
        </FlexBox>
        <FlexBox jc={contentWidth > 530 ? "space-between" : "start"} fullWidth wrap gap>
          <FlexBox jc="start">
            <GeoTargetTypeFilter
              id="geo-target-list-filter-type"
              label={translate("campaign.geo-targets.list.filter.type")}
              value={typeFilter}
              onChange={newFilterTypes => setTypeFilter(newFilterTypes)}
              disabled={!hasGeoTargets(form)}
              sx={{ minWidth: "13rem" }}
            />
          </FlexBox>
          <FlexBox gap wrap={contentWidth < 550} jc={contentWidth < 550 ? "start" : undefined}>
            <GeoTargetsImportButton />
            <GeoTargetsExportButton form={form} />
            <Button
              id="geo-target-delete-all-button"
              variant="outlined"
              disabled={selectedItemIds.length === 0}
              sx={{
                "minWidth": "13rem !important",
                "color": theme => theme.palette.primary.main,
                "&:hover": {
                  color: theme => theme.palette.specialText,
                  backgroundColor: theme => theme.palette.primary.main
                }
              }}
              startIcon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => {
                messageSVC.confirmMessage({
                  title: translate("campaign.geo-targets.delete.all.confirm.title"),
                  addGenericConfirmMessage: true,
                  message: translate("campaign.geo-targets.delete.confirm.message"),
                  onSuccess: () => deleteSelectedGeoTargets()
                })
              }}>
              {translate("button.delete.selection")}
            </Button>
          </FlexBox>
        </FlexBox>
        <DataGrid
          rows={items}
          checkboxSelection
          isRowSelectable={() => !isDeletingSelection}
          columns={columns}
          disableColumnFilter
          getRowClassName={params =>
            params.row.type === GEO_TARGETS_TYPES.COORDINATE && (params.row as any).coordinate.id === toUpdateEntityId
              ? "Mui-hovered"
              : ""
          }
          disableColumnMenu
          keepNonExistentRowsSelected
          selectionModel={selectedItemIds}
          className="geo-targets-form-list"
          rowHeight={30}
          autoPageSize
          onRowClick={params => onItemClick(params.row)}
          onSelectionModelChange={newSelectedItemIds =>
            setGeoTargetsForm({ selection: getSelectionFromItemIds(newSelectedItemIds as string[]) })
          }
          disableSelectionOnClick
          localeText={getDataGridTexts("campaign.geo-targets.list.no.row")}
          sx={{
            height: listHeightInRem,
            minHeight: listHeightInRem,
            maxHeight: listHeightInRem
          }}
        />
        {isDeletingSelection && <TestHiddenData id="campaign-geo-targets-list-is-deleting-selection" />}
      </Box>
    )
  }
)

export const GeoTargetsList = connect(
  (state: IRootState): IGeoTargetsListConnectProps => ({
    geoTargetsForm: getGeoTargetsFormSafely(state),
    contentWidth: state.ui.contentWidth
  })
)(GeoTargetsListFn)
